import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, Event as RouterEvent, RouterModule } from "@angular/router";
import {
  APIService,
  AuthService,
  ClientRole,
  CurrentUser,
  FormComponent,
  LocalStorageService,
  ModalComponent,
  NotificationBellComponent,
  NotificationBellService,
  NotificationService,
  PasswordPolicy
} from "@fourcomply-admin/shared";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { pipe, Subject, takeUntil } from "rxjs";
import { filter } from "rxjs/operators";
import { SYSTEM_ROLE_LABELS } from "../roles/permission-catalog";

/**
 * The slice of the Auth0 profile the header reads. `AuthService.userProfile$` emits the raw
 * Auth0 user augmented with `tenants` / `currentTenant`; everything here is optional because
 * which claims are present depends on how the account was created.
 */
export interface HeaderUserProfile {
  currentTenant?: HeaderTenant;
  email?: string;
  family_name?: string;
  given_name?: string;
  name?: string;
  nickname?: string;
  tenants?: HeaderTenant[];
}

/**
 * One tenant as AuthService builds it from the token's per-tenant role claim
 * (`AuthService.getTenantsFromUser`). `name` carries the "Name / env" form the switcher trims.
 */
export interface HeaderTenant {
  name?: string;
  roles?: string[] | string;
  tenant_id?: string;
}

@Component({
  imports: [
    CommonModule,
    FormComponent,
    FormsModule,
    MatFormFieldModule,
    MatMenuModule,
    MatSelectModule,
    MatTooltipModule,
    ModalComponent,
    NotificationBellComponent,
    RouterModule,
    TranslateModule
  ],
  selector: "app-header",
  standalone: true,
  styleUrls: ["header.component.scss"],
  templateUrl: "header.component.html"
})
export class HeaderComponent implements OnInit, OnDestroy {
  /** The one external page the account menu links to. */
  private static readonly docsUrl: string = 'http://docs.4comply.io';

  @ViewChild("formClient", { static: true })
  formClient: FormComponent;
  @ViewChild("modalClient", { static: true })
  public modalClient: ModalComponent;
  @ViewChild("modalPassword", { static: true })
  public modalPassword: ModalComponent;
  @Input() appTitle: string;
  navOpen = true;
  fieldsFormFields: FormlyFieldConfig[] = [];
  modelFormFields: any = {};
  currentTenant: any;
  selectLang: string = "en";
  selectLangLabel: string = "English";
  selectLangImage: string = "flagUS.png";
  TransLang: any[] = [];
  userTooltipString: string = "";
  settingsTooltipString: string = "";
  docsTooltipString: string = "";
  logoutTooltipString: string = "";

  langs: Array<{ label: string, value: string, image: string }> = [
    { label: "English", value: "en", image: "flagUS.png" },
    { label: "Español", value: "es", image: "flagMX.png" },
    { label: "हिंद", value: "in", image: "flagIN.png" },
    { label: "Italiano", value: "it", image: "flagIT.png" },
    { label: "Français", value: "fr", image: "flagFR.png" },
    { label: "Português", value: "pt", image: "flagPT.png" },
    { label: "Deutsch", value: "de", image: "flagDE.png" },
    { label: "Nederlands", value: "nl", image: "flagNL.png" },
    { label: "日本語", value: "jp", image: "flagJP.png" },
    { label: "简体中文", value: "ch", image: "flagCH.png" },
    { label: "繁体中文", value: "tw", image: "flagTW.png" },
  ];

  /** Tenant roles (system + custom), used to name the signed-in user's role. Empty until loaded. */
  public roles: ClientRole[] = [];

  /**
   * The signed-in user as 4Comply knows them, for the active tenant. This — not the Auth0
   * profile — is the source for the name and the role: Auth0 writes first/last name to
   * `user_metadata`, which no Rule copies into the ID token, and sets the `name` claim to the
   * email address. Undefined until `GET v1/clientuser/me` answers, or if it fails.
   */
  public currentUser: CurrentUser | undefined;

  /* Change-password dialog state. The values live only for the life of the dialog and are
     cleared on close; nothing is stored, logged or sent anywhere but the endpoint. Visibility
     belongs to the shared ModalComponent, not to a flag here. */
  public newPassword: string = "";
  public confirmPassword: string = "";
  public passwordError: string = "";
  public passwordSubmitting: boolean = false;

  /** The tenant's Auth0 rules, loaded when the dialog opens. Undefined = rules not stated. */
  public passwordPolicy: PasswordPolicy | undefined;

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    public auth: AuthService,
    public api: APIService,
    public translate: TranslateService,
    private localStorage: LocalStorageService,
    private router: Router,
    private notification: NotificationService,
    private readonly bell: NotificationBellService
  ) {
    this.submitFormFields = this.submitFormFields.bind(this);
    this.getTransLanguage();
    this.translate = translate;
    this.selectLang = this.localStorage.get("lang")
      ? this.localStorage.get("lang")
      : "en";
    this.processLanguage();
  }

  public ngOnInit(): void {
    this.auth.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: HeaderUserProfile): void => {
        // Waiting for currentTenant is not tidiness: UserInterceptor reads the tenant off this
        // same profile, so firing earlier sends the request with no tenant_id header.
        if (!profile?.currentTenant) {
          return;
        }
        this.loadCurrentUser(profile);
        this.bell.start();
      });

    // Refreshes the unread badge after every navigation. This is also the retry path for a
    // catalog call that errored with something other than 404 (NotificationBellService.refresh()
    // re-attempts start() while the feature state is unresolved).
    this.router.events
      .pipe(
        filter((event: RouterEvent): boolean => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((): void => this.bell.refresh());
  }

  /**
   * Fetches the caller's own record once per header instance, then decides whether the tenant's
   * role list is needed.
   *
   * The order matters and was got wrong once: deciding before /me answers reads the Auth0 claim,
   * which carries the pre-RBAC model. A user whose claim says `admin` but whose real role is
   * custom would skip the fetch, and the menu would then print the raw key
   * (`USER.CUSTOM-4-MORE-BASIC_LEVEL`) forever. The decision therefore lives here, after the
   * authoritative answer, and runs again on the fallback path when the call fails.
   */
  private loadCurrentUser(profile: HeaderUserProfile): void {
    if (this.currentUser) {
      return;
    }
    this.api.clientUsers.me()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (): void => this.loadRolesIfCustom(profile),
        next: (user: CurrentUser | undefined): void => {
          // 204 (no record for this caller in this tenant) arrives as a null body.
          if (user) {
            this.currentUser = user;
          }
          this.loadRolesIfCustom(profile);
        }
      });
  }

  /**
   * The role list only exists to give a CUSTOM role its name; the four system roles already have
   * their labels locally. Skipping the request for them is also what keeps an API older than the
   * RBAC endpoints from popping a 404 toast on every page load — AppHttpInterceptor raises that
   * toast before any subscriber's error handler runs (app-http-interceptor.service.ts:48).
   */
  private loadRolesIfCustom(profile: HeaderUserProfile): void {
    const key: string = this.getRoleKey(profile);
    if (!key || SYSTEM_ROLE_LABELS[key] || this.roles.length) {
      return;
    }
    this.loadRoles();
  }

  /** Fetches the tenant's roles so a custom role can be shown by name rather than by key. */
  private loadRoles(): void {
    this.api.clientRoles.listCached()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (): void => undefined,
        next: (roles: ClientRole[]): void => {
          this.roles = roles || [];
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /* ============== User menu ============== */

  /**
   * The name to show in the bar. Deliberately never the email address: Auth0 sets the `name`
   * claim to the address for accounts created without a profile name, so falling back to `name`
   * alone would print the very thing this menu exists to stop printing.
   */
  public getDisplayName(profile: HeaderUserProfile | undefined): string {
    // 4Comply's own record wins: it is the only source that actually holds the person's name.
    const first: string = (this.currentUser?.first_name || "").trim();
    const last: string = (this.currentUser?.last_name || "").trim();
    if (first || last) {
      return [first, last].filter((part: string): boolean => !!part).join(" ");
    }
    if (!profile) {
      return "";
    }
    const given: string = (profile.given_name || "").trim();
    const family: string = (profile.family_name || "").trim();
    if (given || family) {
      return [given, family].filter((part: string): boolean => !!part).join(" ");
    }
    const name: string = (profile.name || "").trim();
    if (name && !name.includes("@")) {
      return name;
    }
    const nickname: string = (profile.nickname || "").trim();
    if (nickname) {
      return nickname;
    }
    const email: string = (profile.email || "").trim();
    return email.includes("@") ? email.split("@")[0] : email;
  }

  /** Up to two uppercase initials taken from the display name. */
  public getInitials(profile: HeaderUserProfile | undefined): string {
    return this.getDisplayName(profile)
      .split(" ")
      .filter((part: string): boolean => !!part)
      .slice(0, 2)
      .map((part: string): string => part[0].toUpperCase())
      .join("");
  }

  /**
   * The role key for the ACTIVE tenant. One login can hold a different role per tenant, so this
   * must never read a role from the profile root or from the tenant list.
   */
  public getRoleKey(profile: HeaderUserProfile | undefined): string {
    // user_level on the 4Comply record is authoritative for this tenant; the Auth0 claim is the
    // pre-RBAC model and only stands in until /me answers.
    const stored: string = (this.currentUser?.user_level || "").trim();
    if (stored) {
      return stored;
    }
    const roles: string[] | string | undefined = profile?.currentTenant?.roles;
    if (!roles) {
      return "";
    }
    return (Array.isArray(roles) ? roles[0] : roles) || "";
  }

  /**
   * Display label for the active tenant's role: a translation key for the four system roles, a
   * custom role's own name, or the legacy USER.<KEY>_LEVEL key when the roles list has not
   * loaded (or does not contain the key). Empty when the tenant carries no role.
   */
  public getRoleLabel(profile: HeaderUserProfile | undefined): string {
    const key: string = this.getRoleKey(profile);
    if (!key) {
      return "";
    }
    const system: { descKey: string; label: string } | undefined = SYSTEM_ROLE_LABELS[key];
    if (system) {
      return system.label;
    }
    const match: ClientRole | undefined = this.roles.find((role: ClientRole): boolean => role.key === key);
    return match ? match.name : `USER.${key.toUpperCase()}_LEVEL`;
  }

  /* ============== Change password ============== */

  /** Opens the change-password dialog with empty fields, and states the tenant's real rules. */
  public openPasswordModal(): void {
    this.resetPasswordForm();
    this.loadPasswordPolicy();
    this.modalPassword?.openModal();
  }

  /**
   * Loads the rules once per session. A failure is deliberately silent: not knowing the rules
   * must not stop someone changing their password — the checklist simply is not shown and Auth0
   * remains the authority, exactly as before.
   */
  private loadPasswordPolicy(): void {
    if (this.passwordPolicy) {
      return;
    }
    this.api.clientUsers.passwordPolicy()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (): void => undefined,
        next: (policy: PasswordPolicy): void => {
          this.passwordPolicy = policy;
        }
      });
  }

  /**
   * The rules each Auth0 level implies, as Auth0 documents them. `good` and `excellent` are the
   * two that require "3 of the 4" character classes rather than all of them, which is why the
   * checklist cannot be a flat list of booleans.
   */
  public get passwordRules(): Array<{ key: string; met: boolean; params?: object }> {
    const policy: PasswordPolicy | undefined = this.passwordPolicy;
    if (!policy) {
      return [];
    }
    const value: string = this.newPassword || "";
    const level: string = (policy.policy || "good").toLowerCase();
    const rules: Array<{ key: string; met: boolean; params?: object }> = [
      { key: "USER.PASSWORD_RULE_LENGTH", met: value.length >= policy.min_length, params: { count: policy.min_length } }
    ];
    if (level === "none" || level === "low") {
      return rules;
    }
    if (level === "fair") {
      rules.push({ key: "USER.PASSWORD_RULE_LOWER", met: /[a-z]/.test(value) });
      rules.push({ key: "USER.PASSWORD_RULE_UPPER", met: /[A-Z]/.test(value) });
      rules.push({ key: "USER.PASSWORD_RULE_NUMBER", met: /[0-9]/.test(value) });
      return rules;
    }
    rules.push({ key: "USER.PASSWORD_RULE_THREE_OF_FOUR", met: this.characterClassCount(value) >= 3 });
    if (level === "excellent") {
      rules.push({ key: "USER.PASSWORD_RULE_NO_REPEATS", met: !this.hasTripleRepeat(value) });
    }
    return rules;
  }

  /**
   * True when any character appears three times in a row, which Auth0's "excellent" policy
   * forbids. Written as a scan rather than a back-reference regex: the regex form is easy to
   * mangle when this file is generated or edited, and it is no clearer than the loop.
   */
  private hasTripleRepeat(value: string): boolean {
    for (let i: number = 2; i < value.length; i++) {
      if (value[i] === value[i - 1] && value[i] === value[i - 2]) {
        return true;
      }
    }
    return false;
  }

  /** How many of Auth0's four character classes the value uses. */
  private characterClassCount(value: string): number {
    return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/]
      .filter((pattern: RegExp): boolean => pattern.test(value)).length;
  }

  public trackByRuleKey(_index: number, rule: { key: string }): string {
    return rule.key;
  }

  /** True when every stated rule is met. Vacuously true when the rules could not be loaded. */
  public get meetsPolicy(): boolean {
    return this.passwordRules.every((rule: { met: boolean }): boolean => rule.met);
  }

  /** Closes the dialog. The shared modal answers back through `closer`, which clears the form. */
  public closePasswordModal(): void {
    this.modalPassword?.closeModal();
    this.onPasswordModalClosed();
  }

  /**
   * Runs whenever the dialog closes, however it closed — the Cancel button, the X, the overlay
   * or Escape. Clearing here rather than in closePasswordModal() is what guarantees the typed
   * password never survives a dismissal the component did not initiate.
   */
  public onPasswordModalClosed(): void {
    this.resetPasswordForm();
  }

  private resetPasswordForm(): void {
    this.newPassword = "";
    this.confirmPassword = "";
    this.passwordError = "";
    this.passwordSubmitting = false;
  }

  /**
   * The rules are checked here too, but they are never invented here: they come from the tenant's
   * own Auth0 connection, so the dialog states what Auth0 will actually enforce instead of a
   * guess that drifts. Auth0 remains the authority — this only stops the user discovering the
   * rules by trial and error.
   */
  public get passwordsMatch(): boolean {
    return !!this.newPassword && this.newPassword === this.confirmPassword;
  }

  /** Save is enabled only when the two fields match AND every stated rule is met. */
  public get canSubmitPassword(): boolean {
    return this.passwordsMatch && this.meetsPolicy && !this.passwordSubmitting;
  }

  /**
   * Sets a new password on the caller's own account.
   *
   * By product decision this does not ask for the current password (see the API's
   * ChangeOwnPasswordDTO): anyone holding this session can change it. The endpoint resolves the
   * account from the JWT, so nothing identifying the user is sent from here.
   */
  public submitPassword(): void {
    if (!this.canSubmitPassword) {
      return;
    }
    this.passwordSubmitting = true;
    this.passwordError = "";
    this.api.clientUsers.changeOwnPassword(this.newPassword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error: { errors?: string[] }): void => {
          this.passwordSubmitting = false;
          // The API answers { errors: [ ... ] }, the one shape CustomErrorHandlerService keeps
          // intact; anything else it collapses into Angular's "Http failure response for <url>"
          // text, which is what used to end up in front of the user. The value is either an
          // i18n key we own or Auth0's own policy sentence, and the template translates it —
          // translate() returns its input unchanged when no key matches, so both work.
          this.passwordError = error?.errors?.[0] || "USER.PASSWORD_CHANGE_FAILED";
        },
        next: (): void => {
          this.passwordSubmitting = false;
          this.closePasswordModal();
          this.notification.success("USER.PASSWORD_CHANGED");
        }
      });
  }

  processLanguage() {
    this.translate.use(this.selectLang);
    this.selectLangLabel = this.langs.find(
      (i) => i.value === this.selectLang
    ).label;
    this.selectLangImage = this.langs.find(
      (i) => i.value === this.selectLang
    ).image;
  }

  translateTooltips() {
    this.userTooltipString = this.translate.instant(
      "BASIC_ENTITIES_PLURAL.USERS"
    );
  }

  getTransLanguage() {
    this.TransLang = this.langs;
  }

  setTransLanguage(event: MatSelectChange) {
    this.selectLang = event.value;
    this.localStorage.set("lang", event.value);
    this.processLanguage();
  }

  // Borderless menu uses a direct click handler instead of mat-select's change event.
  setTransLanguageDirect(value: string) {
    this.selectLang = value;
    this.localStorage.set("lang", value);
    this.processLanguage();
  }

  trackByLangValue(_index: number, lang: { value: string }) {
    return lang.value;
  }

  public trackByTenantId(_index: number, tenant: HeaderTenant): string | number {
    return tenant?.tenant_id ?? tenant?.name ?? _index;
  }

  /**
   * The tenant's display name, without the environment suffix.
   *
   * Tenant names are stored as "Name / env" (e.g. "XDA-DevAccount / xdadev"). The switcher shows
   * only the part before the slash — the suffix is an internal identifier and means nothing to
   * the person choosing an account.
   *
   * Note the consequence: two tenants whose names differ ONLY after the slash now read
   * identically in the switcher. Nothing in the product prevents that, so if it ever happens the
   * fix is to give them distinct names, not to put the suffix back.
   */
  public getTenantPrimary(tenant: HeaderTenant | undefined): string {
    const raw: string = (tenant?.name ?? "").toString();
    const idx: number = raw.indexOf("/");
    return (idx === -1 ? raw : raw.slice(0, idx)).trim();
  }

  submitFormFields() {
    this.auth.userProfile$.pipe(takeUntil(this.destroy$)).subscribe(
      pipe((res: any) => {
        this.api.clients
          .createClient(
            this.modelFormFields.name,
            this.modelFormFields.environment,
            res
          )
          .subscribe((newClient) => {
            const intermediate: string = location.href.split("?tenant=")[0];
            location.href = `${intermediate}?tenant=${newClient.client.id}`;
          });
      })
    );
  }

  initFormFieldModalForm() {
    this.fieldsFormFields = [
      {
        key: "name",
        type: "input",
        props: {
          required: true,
          label: "GENERIC.NAME",
        },
      },
      {
        key: "environment",
        type: "input",
        props: {
          required: true,
          label: "HEADER.COMPANY_IDENTIFIER",
        },
      },
    ];
    this.formClient.updateFields(this.fieldsFormFields);
  }

  startClientModal() {
    this.initFormFieldModalForm();
    this.modalClient.openModal();
  }

  closeModal() {
    this.formClient.options.resetModel();
    this.initFormFieldModalForm();
  }

  changeTenant(tenant: any) {
    if (tenant === "0000000000") {
      this.startClientModal();
    } else {
      this.bell.stop();
      this.auth.changeTenant(tenant[0]).subscribe();
    }
  }

  /** Stops the bell's polling before ending the session; a dead component must not keep polling. */
  /**
   * Opens the product documentation in a new tab.
   *
   * `noopener,noreferrer` explicitly: the icon this replaces was a `target="_blank"` anchor with
   * neither, so the opened page could reach back through `window.opener`. Browsers imply noopener
   * for `target="_blank"` these days, but this does not depend on that.
   */
  public openDocs(): void {
    window.open(HeaderComponent.docsUrl, '_blank', 'noopener,noreferrer');
  }

  public logout(): void {
    this.bell.stop();
    this.auth.logout();
  }
}
